import useMeasure from 'react-use-measure'
import { Organization } from '../../../types'
import { useData } from '../contexts/DataContextProvider'
import { useMemo } from 'react'
import styles from './AllOrgsDotBar.module.scss'
import { computeDotBarLayout, orgOrInitiativeColor } from './utils'
import DotBarChart from './DotBarChart'
import Circle from './Circle'
import { CircleProps } from '../models'
import { useInView } from '@react-spring/web'
import { CIRCLES_ANIMATE_ONCE } from '../constants'
import { Color } from '../../../constants'

export default function AllOrgsDotBar() {
  const { data = [] }: { data: Organization[] } = useData()
  const [ref, { width = 1, height = 1 }] = useMeasure()
  const [inViewRef, inView] = useInView({
    amount: 0.1,
    once: CIRCLES_ANIMATE_ONCE,
  })

  const circles: CircleProps[] = useMemo(() => {
    if (!data || data.length === 0 || width <= 0 || height <= 0) {
      return []
    }
    return computeDotBarLayout({
      width,
      height,
      nCircles: data.length,
    }).map((circle, index) => ({
      ...circle,
      fill: inView
        ? orgOrInitiativeColor({
            initCommTask: data[index].initCommTask,
          })
        : Color.Black,
      id: `circle-${index}`,
    }))
  }, [data, width, height, inView])

  const circleRadius = useMemo(() => circles?.[0]?.r || 0, [circles])
  const circleDiameter = useMemo(() => circleRadius * 2, [circleRadius])

  return (
    <figure className={styles.figureWrapper}>
      <div className={styles.bigNumberWrapper}>
        <p className={styles.bigNumber}>{data.length}</p>
        <p className={styles.bigNumberUnit}>
          Organizations tracked in our tool
        </p>
      </div>
      <div ref={inViewRef} className={styles.dotBarWrapper}>
        <DotBarChart
          ref={ref}
          width={width}
          height={height}
          circles={circles}
        />
      </div>

      <div className={styles.legendWrapper}>
        <div className={styles.legendItemWrapper}>
          <p className={styles.medNumber}>
            {data.filter(({ initCommTask }) => initCommTask).length}
          </p>
          <div className={styles.legendItem}>
            <svg
              viewBox={`0 0 ${circleDiameter} ${circleDiameter}`}
              width={circleDiameter}
              height={circleDiameter}
            >
              <Circle
                x={circleRadius}
                y={circleRadius}
                r={circleRadius}
                fill={orgOrInitiativeColor({ initCommTask: true })}
              />
            </svg>
            <p className={styles.legendText}>Initiatives</p>
          </div>
        </div>

        <div className={styles.legendItemWrapper}>
          <p className={styles.medNumber}>
            {data.filter(({ initCommTask }) => !initCommTask).length}
          </p>
          <div className={styles.legendItem}>
            <svg
              viewBox={`0 0 ${circleDiameter} ${circleDiameter}`}
              width={circleDiameter}
              height={circleDiameter}
            >
              <Circle
                x={circleRadius}
                y={circleRadius}
                r={circleRadius}
                fill={orgOrInitiativeColor({ initCommTask: false })}
              />
            </svg>
            <p className={styles.legendText}>Movement Partners</p>
          </div>
        </div>
      </div>
    </figure>
  )
}
